import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import type { Asset, AssetGroup, FancyCaptionAsset, TextAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import { ASSET_TYPES } from '../video-state';
import type { TextColorHandle, TextColorRole } from '../video-state/serialization/textColorMap';
import { buildGroupColorMap, buildTextColorMap } from '../video-state/serialization/textColorMap';

import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import { checkpointVideo } from 'js/src/data/video-data';
import { setObjectPropertyValue } from 'js/src/editor/util';
import { intToHex, parseHexColor } from 'js/src/libs/color-utils';
import type { RootState } from 'js/src/libs/RootState';
import { saveRawLuminary } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { setTextColorOperation } from 'js/src/luminary/text-editing-operations';

/** Glyph fills — set on the text/caption asset's style, cursor-safe, via setTextColorOperation. */
const GLYPH_FILL_ROLES: ReadonlySet<TextColorRole> = new Set<TextColorRole>([
    'text',
    'highlight',
    'spoken_word',
]);
/** Backdrops/bars a text or group draws — fill + alpha written directly on the backing graphic. */
const BOX_ROLES: ReadonlySet<TextColorRole> = new Set<TextColorRole>([
    'backdrop',
    'underline',
    'accent_bar',
]);

/**
 * What a handle addresses: a text/caption asset (glyph fills + its own boxes) or an asset group (the
 * backdrops/underlines it draws behind its children). A group's backdrop is the group's colour, so
 * it is reached by addressing the group — not by diving into a child text asset.
 */
type ColorTarget =
    | { kind: 'text'; asset: TextAsset | FancyCaptionAsset; map: TextColorHandle[] }
    | { kind: 'group'; asset: AssetGroup; map: TextColorHandle[] };

export const setTextColorSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z
        .string()
        .describe(
            `The ${ASSET_TYPES.TEXT} or ${ASSET_TYPES.FANCY_CAPTION} asset, or the ${ASSET_TYPES.GROUP} whose backdrop you want to recolour.`,
        ),
    handle: z
        .string()
        .describe(
            "The colour handle from that asset's colour map: on a text asset 'text' (glyph fill), 'highlight' (highlighted-runs fill, TEXT only); on an animated caption 'text' and 'spoken_word' (the spoken-word colour); on either a box handle like 'box_1'; on a group a box handle like 'box_1' (the backdrop/underline it draws behind its children).",
        ),
    color: z
        .string()
        .regex(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
        .optional()
        .describe('The new colour, as a hex code.'),
    alpha: z
        .number()
        .gt(0)
        .max(1)
        .optional()
        .describe(
            "New opacity, above 0 up to 1 — box handles only. Adjusts a visible box's opacity (e.g. soften a heavy panel). Can't be set to 0: a box hidden that way drops out of the colour map and can't be reached again.",
        ),
});
export type SetTextColorArgs = z.infer<typeof setTextColorSchema>;

export class SetTextColorTool extends VideoStateTool<typeof setTextColorSchema> {
    protected override readonly schema = setTextColorSchema;
    protected override readonly toolName = TOOL_NAMES.setTextColor;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription = `Set the colour of any part of a ${ASSET_TYPES.TEXT} or ${ASSET_TYPES.FANCY_CAPTION} asset — or the backdrop an enclosing ${ASSET_TYPES.GROUP} draws — by its handle in that asset's colour map: a text's glyph fill (\`text\`), its highlighted-runs fill (\`highlight\`), or a box behind it (\`box_1\`, …; roles backdrop/underline/accent_bar). It also colours animated captions: use \`spoken_word\` for the spoken-word colour (their content is transcript-driven and not editable). A backdrop or underline a group draws behind its children is the GROUP's colour: address the group by name and use its box handle. For a box you can also set \`alpha\` to adjust its opacity. Does NOT create boxes (only recolours ones the design already draws), and can't yet set outlines, shadows, or gradient fills.`;

    public override getToolDisplayName(): string {
        return 'Setting text colour';
    }

    public get statusIcon(): string {
        return 'format_color_fill';
    }

    public async execute(args: SetTextColorArgs, context: ToolExecutionContext) {
        const { asset_name, handle, color, alpha } = args;
        const { parentActionGroup } = context;

        if (color === undefined && alpha === undefined) {
            return 'Error: provide at least one of color or alpha to change.';
        }

        let colorInt: number | undefined;
        if (color !== undefined) {
            const parsed = parseHexColor(color);
            if (parsed === null) {
                return 'Error: color must be a hex string like "#ff0000"';
            }
            colorInt = parsed;
        }

        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[scene_index];

            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId}`;
            }

            const target = this.resolveColorTarget(asset);
            if (!target) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not a ${ASSET_TYPES.TEXT} or ${ASSET_TYPES.FANCY_CAPTION} asset or a ${ASSET_TYPES.GROUP}.`;
            }

            const colorMap = target.map;
            const settableHandles = colorMap
                .filter(
                    (entry) =>
                        GLYPH_FILL_ROLES.has(entry.role) ||
                        (BOX_ROLES.has(entry.role) && entry.fillType !== 'gradient'),
                )
                .map((entry) => entry.handle)
                .join(', ');

            const entry = colorMap.find((candidate) => candidate.handle === handle);
            if (!entry) {
                return `Error: ${asset_name} in scene ${sceneShortId} has no colour handle "${handle}". Settable handles: ${settableHandles}.`;
            }

            // Glyph fills: set through the text-colour operation (cursor-safe, tracks analytics),
            // never a raw write. Alpha doesn't apply — glyph opacity isn't set here.
            if (GLYPH_FILL_ROLES.has(entry.role)) {
                if (colorInt === undefined) {
                    return `Error: the ${entry.role} colour takes a color, not alpha.`;
                }
                const { setTextColor } = makeOperationsCallable(
                    { setTextColorOperation },
                    this.luminary,
                    this.dispatch,
                    () => null as unknown as RootState,
                );
                const path =
                    entry.role === 'text'
                        ? 'normalStyle.fill'
                        : entry.role === 'spoken_word'
                          ? 'spokenWordStyle.fill'
                          : 'highlightStyle.fill';
                setTextColor(scene.id, target.asset.id, path, colorInt, false, parentActionGroup);
                return `Success: Set the ${entry.role} colour of ${asset_name} in scene ${sceneShortId} to ${intToHex(colorInt)}.`;
            }

            // Boxes/panels: recolour or re-alpha the backing graphic.
            if (!BOX_ROLES.has(entry.role)) {
                // A described-but-read-only colour: outline or shadow.
                return `Error: "${handle}" is the ${entry.role} colour of ${asset_name} in scene ${sceneShortId}; no tool can change it yet. Settable handles: ${settableHandles}.`;
            }

            // A gradient-filled box ignores its flat fill/alpha at render time (the renderer paints
            // the gradient texture instead), so a write would land on a property nothing reads and
            // still report success — the exact wasted round-trip this tool exists to avoid.
            if (entry.fillType === 'gradient') {
                return `Error: ${handle} on ${asset_name} in scene ${sceneShortId} is a gradient fill, whose colour and opacity can't be set here. Change the text colour (handle "text") instead.`;
            }

            const owner = entry.ownerAssetId ? scene.getAssetById(entry.ownerAssetId) : undefined;
            if (!owner || !entry.valuePath || !entry.alphaPath) {
                return `Error: could not resolve the owner of ${handle} on ${asset_name} in scene ${sceneShortId}.`;
            }

            // Write fill/alpha directly rather than through setBackgroundAssetColor/AlphaOperation:
            // those are the scene-background-asset path and latch a `background_color` user override
            // on the scene (excluding it from group-wide colour application and flipping the UI
            // indicator) — none of which touching a text box should do.
            const changes: string[] = [];
            if (colorInt !== undefined) {
                setObjectPropertyValue(owner, entry.valuePath, colorInt);
                changes.push(`fill to ${intToHex(colorInt)}`);
            }
            if (alpha !== undefined) {
                setObjectPropertyValue(owner, entry.alphaPath, alpha);
                changes.push(`alpha to ${alpha}`);
            }

            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );

            return `Success: Set ${changes.join(' and ')} for ${handle} (${entry.role}) on ${asset_name} in scene ${sceneShortId}.`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting text colour: ${errorMessage}`;
        }
    }

    /**
     * The asset a handle addresses and the colour map it was serialized from: a text or animated
     * caption asset carries its glyph fills and its own boxes; a group carries the
     * backdrops/underlines it draws behind its children. A group is addressed directly rather than
     * diving into a child, so a group's shared backdrop is reached once, through the group — the way
     * it is serialized.
     */
    private resolveColorTarget(asset: Asset): ColorTarget | null {
        if (VideoData.isTextAsset(asset) || VideoData.isFancyCaptionAsset(asset)) {
            return { kind: 'text', asset, map: buildTextColorMap(asset) };
        }
        if (VideoData.isAssetGroup(asset)) {
            return { kind: 'group', asset, map: buildGroupColorMap(asset) };
        }
        return null;
    }
}
